import { Injectable, OnDestroy, signal } from '@angular/core';
import * as pdfjs from 'pdfjs-dist';
import { PDFPageProxy } from 'pdfjs-dist';

@Injectable()
export class PdfService implements OnDestroy {
  private width!: number;
  private canvasRef!: HTMLCanvasElement;
  private PDFDocumentProxy!: pdfjs.PDFDocumentProxy;
  private renderTask!: pdfjs.RenderTask | null;

  totalPages = signal<number>(1);

  constructor() {
    pdfjs.GlobalWorkerOptions.workerSrc = '/assets/scripts/pdf.worker.mjs';
  }

  /**
   * Initializes the PDF service with a source, a canvas reference, and a page width.
   *
   * @param {string} src - The source of the PDF.
   * @param {HTMLCanvasElement} canvasRef - A reference to the canvas element where the PDF will be rendered.
   * @param {number} pageWidth - The width of the page.
   *
   * This function sets the canvas reference and the page width, and gets the PDF document from the source.
   * It then sets the total number of pages in the PDF document.
   *
   * @returns {Promise<void>} A promise that resolves when the PDF document has been loaded.
   */
  async init(
    src: string,
    canvasRef: HTMLCanvasElement,
    pageWidth: number,
  ): Promise<void> {
    this.canvasRef = canvasRef;
    this.width = pageWidth;
    this.PDFDocumentProxy = await pdfjs.getDocument({
      url: src,
      isEvalSupported: false,
    }).promise;
    this.totalPages.set(this.PDFDocumentProxy.numPages);
  }

  /**
   * It takes  number and returns a promise that resolves to a canvas with page.
   * @param {number} pageNumber - page number
   * @returns HTMLCanvasElement
   */
  async renderPage(pageNumber: number): Promise<HTMLCanvasElement> {
    if (!this.PDFDocumentProxy || !this.canvasRef) {
      throw new Error('PDF file is not loaded');
    }
    const page = await this.PDFDocumentProxy.getPage(pageNumber);
    const scale = this.updateCanvas(page);
    if (this.renderTask) {
      this.renderTask.cancel();
    }
    this.renderTask = page.render({
      canvasContext: this.canvasRef.getContext(
        '2d',
      ) as CanvasRenderingContext2D,
      viewport: page.getViewport({ scale }),
    });
    return this.renderTask.promise.catch((err: any) => {
      console.error(err);
      this.renderTask = null;

      if (err.name === 'RenderingCancelledException') {
        return this.renderPage(pageNumber);
      }
      return err;
    });
  }

  /**
   * It takes a page from the PDF document and renders it to a canvas element
   * @param page - The page to render.
   * @returns A promise.
   *
   * @memberof PdfHelperService
   */
  private updateCanvas(page: PDFPageProxy): number {
    const vp = page.getViewport({ scale: 1 });
    const scale = this.width / vp.width;
    const pageVp = page.getViewport({ scale });
    this.canvasRef.width = pageVp.width;
    this.canvasRef.height = pageVp.height;
    return scale;
  }

  ngOnDestroy(): void {
    this.PDFDocumentProxy?.cleanup();
    this.PDFDocumentProxy?.destroy();
  }
}
